import { forwardRef, useRef, useMemo, useCallback, useEffect, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import * as d3 from 'd3'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { formatSVG } from '@aytm/app/stats/lib/render/svg-export'
import { useExport } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useExport'
import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import { BPM_EXPORT_OPTIONS } from '@aytm/app/stats/page/question-views/bpm/lib/exportOptions'

import {
  computeAxes,
  computePoints
} from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsData'
import { createProjection } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsProjection'
import { drawStatic } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsDraw'
import {
  updateAxisLabels,
  updateChartLabels
} from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsLabels'
import {
  applyAxisHighlight,
  applySelection
} from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsSelection'
import { attachInteractions } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsInteractions'

import style from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/styles/Chart.module.scss'
import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'

const CHART_HEIGHT = 426
const COMPLETE_DELAY = 300

const resolveCenter = (mdsCenter, width, height) => ({
  x: width / 2,
  y: height / 2,
  xm: (Number.isInteger(mdsCenter?.x) && mdsCenter.x) || 0,
  ym: (Number.isInteger(mdsCenter?.y) && mdsCenter.y) || 0
})

const BPMMdsChart = forwardRef(
  (
    {
      questionId,
      questionKey,
      questionVariants,
      batches,
      answer,
      colorTheme,
      chartLegendMode,
      chartMode,
      features,
      mdsBrandsValue = [],
      mdsAttributesValue = [],
      colorGradient = () => '#cccccc',
      onBrandsChange,
      onComplete,
      onChartReady
    },
    ref
  ) => {
    const apiRef = useRef({})

    // The caller may own the box -- it hands its ref in, and the width is measured off the same node.
    const { componentRef, width } = useComponentWidth({ ref, defaultWidth: 814 })
    const svgRef = useRef(null)
    const sceneRef = useRef(null) // { projection, center } from the last geometry build

    // Adapter so the Highcharts useExport hook can read our own <svg> (chart.container.firstChild)
    const exportAdapterRef = useRef({
      chart: {
        container: {
          get firstChild() {
            return svgRef.current
          }
        }
      }
    })
    const { exportOptions, handleExport } = useExport({
      questionKey,
      chartRef: exportAdapterRef,
      qType: 'bpm',
      features,
      baseOptions: BPM_EXPORT_OPTIONS
    })

    // Latest selection values read by both effects without forcing a geometry rebuild
    const selectionRef = useRef({})
    selectionRef.current = { mdsBrandsValue, mdsAttributesValue, colorGradient }

    // Selection toggle bookkeeping + drag guard for the imperative pointer handlers
    const stateRef = useRef({ selected: [], selectedState: undefined, selectedSingle: undefined })
    const dragRef = useRef({ dragging: false })
    const onBrandsChangeRef = useRef(onBrandsChange)
    onBrandsChangeRef.current = onBrandsChange

    const { axises, labels } = useMemo(
      () => computeAxes({ answer, batches, center: answer?.mds?.center }),
      [answer, batches]
    )
    const points = useMemo(() => computePoints({ answer, questionVariants }), [answer, questionVariants])
    const defaultBrands = useMemo(() => questionVariants.map(v => String(v.position)), [questionVariants])

    // Re-style selection state (petals + point/axis visibility) on the existing scene
    const applyDynamic = useCallback(
      (svgNode, scene) => {
        const selection = selectionRef.current

        // mirror legacy @chart.selected / selectedSingle bookkeeping (length 0 leaves single as-is)
        stateRef.current.selected = selection.mdsBrandsValue
        if (selection.mdsBrandsValue.length === 1) stateRef.current.selectedSingle = selection.mdsBrandsValue[0]
        else if (selection.mdsBrandsValue.length > 1) stateRef.current.selectedSingle = null

        applySelection(svgNode, {
          axises,
          points,
          projection: scene.projection,
          center: scene.center,
          answer,
          mdsBrandsValue: selection.mdsBrandsValue,
          mdsAttributesValue: selection.mdsAttributesValue,
          questionId,
          colorGradient: selection.colorGradient
        })
        applyAxisHighlight(svgNode, {
          axises,
          mdsAttributesValue: selection.mdsAttributesValue,
          questionId,
          selectedCount: selection.mdsBrandsValue.length
        })
      },
      [axises, points, answer, questionId]
    )

    // PPTX bridge (pptx.coffee -> block.formatSVG). The svg transform + serialization live here
    // (lib/svg-export formatSVG); coffee just forwards the string. Drops the download affordance and
    // lays an opaque backdrop so the exported image is not transparent.
    apiRef.current.formatSVG = () => {
      const source = svgRef.current
      if (!source) return null

      const svg = d3.select(source.cloneNode(true))

      svg.selectAll('g.download').remove()

      svg
        .insert('rect', ':first-child')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', source.width.baseVal.value)
        .attr('height', source.height.baseVal.value)
        .attr('fill', '#ffffff')

      return formatSVG(svg.node())
    }

    useEffect(() => {
      onChartReady?.(apiRef.current)
    }, [onChartReady])

    // Geometry build: static scene + labels + collision layout (NOT on selection change)
    useLayoutEffect(() => {
      const svgNode = svgRef.current
      if (!svgNode) return

      if (isMapOrNone(chartMode)) {
        d3.select(svgNode).selectAll('*').remove()
        sceneRef.current = null
        onComplete?.()
        return
      }

      const projection = createProjection({ width, height: CHART_HEIGHT, axises, points })
      const center = resolveCenter(answer?.mds?.center, width, CHART_HEIGHT)
      sceneRef.current = { projection, center }

      drawStatic(svgNode, {
        id: questionId,
        width,
        height: CHART_HEIGHT,
        axises,
        points,
        projection,
        center,
        colors: colorTheme,
        chartLegendMode
      })

      updateAxisLabels(svgNode, {
        axisesLabels: labels,
        axises,
        mdsAttributesValue: selectionRef.current.mdsAttributesValue
      })
      updateChartLabels(svgNode, { width, height: CHART_HEIGHT, selected: selectionRef.current.mdsBrandsValue })
      applyDynamic(svgNode, sceneRef.current)

      attachInteractions(svgNode, {
        points,
        defaultBrands,
        getSelected: () => selectionRef.current.mdsBrandsValue,
        stateRef,
        dragRef,
        onBrandsChange: next => onBrandsChangeRef.current?.(next)
      })

      const timer = setTimeout(() => onComplete?.(), COMPLETE_DELAY)
      return () => clearTimeout(timer)
    }, [
      width,
      axises,
      labels,
      points,
      defaultBrands,
      answer,
      chartMode,
      chartLegendMode,
      colorTheme,
      questionId,
      onComplete,
      applyDynamic
    ])

    // Selection-only restyle: cheap, never rebuilds geometry (labels must not jump)
    useLayoutEffect(() => {
      const svgNode = svgRef.current
      const scene = sceneRef.current

      if (!svgNode || !scene || isMapOrNone(chartMode)) return

      applyDynamic(svgNode, scene)
    }, [mdsBrandsValue, mdsAttributesValue, chartMode, applyDynamic])

    return (
      <div ref={componentRef} className={style.chart}>
        <div className={style.export}>
          <ExportDropdown options={exportOptions} onExport={handleExport} />
        </div>
        <div id={questionId == null ? undefined : `chart_question_${questionId}`}>
          {/* The export posts this node's outerHTML, which names the namespaces only when they are attributes */}
          <svg
            ref={svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          />
        </div>
      </div>
    )
  }
)

BPMMdsChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array.isRequired,
  batches: PropTypes.array.isRequired,
  answer: answerPropType,
  colorTheme: PropTypes.array.isRequired,
  chartLegendMode: PropTypes.string,
  chartMode: PropTypes.string,
  features: PropTypes.object,
  mdsBrandsValue: PropTypes.arrayOf(PropTypes.string),
  mdsAttributesValue: PropTypes.arrayOf(PropTypes.string),
  colorGradient: PropTypes.func,
  onBrandsChange: PropTypes.func,
  onComplete: PropTypes.func,
  onChartReady: PropTypes.func
}

BPMMdsChart.displayName = 'BPMMdsChart'

export default BPMMdsChart
